import { Component, OnInit } from '@angular/core';
import {
    AbstractControl,
    UntypedFormArray,
    UntypedFormBuilder,
    UntypedFormGroup,
    ValidationErrors,
    ValidatorFn,
    Validators,
} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import _ from 'lodash';
import { Observable } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { DropDownOption } from '../common/entities/dropdown/dropDown-options';
import { LeaveImportModel } from '../common/entities/leave-import/leave-import.model';
import {
    ExcelColumn,
    LeaveImportTemplate, 
    Parameters,
} from '../common/entities/leave-import/template.model';
import {
    AvisoBaja,
    SinTelefono,
    Tareas,
    TipoDeImportacion,
} from '../common/enums/leave-import.enums';
import { CommonDialogService } from '../common/services/common-dialog.service';
import { PageLoaderService } from '../common/services/page-loader.service';
import { SharedService } from '../common/services/shared-service';
import { LeaveImportResult } from '../common/types/leave-import-results.type';
import { FileExportService } from '../selection-generic/services/file-export.service';
import { ImportarEmpresaModalComponent } from '../staff-count/staff-import-view/popups/importar-empresa-modal/importar-empresa-modal.component';
import { CriteriosComponent } from './popups/criterios/criterios.component';
import { ExcelColumnsService } from './services/excel-columns.service';
import { LeaveImportService } from './services/leave-import.service';

@Component({
    selector: 'app-leave-import',
    templateUrl: './leave-import.component.html',
    styleUrls: ['./leave-import.component.scss'],
    standalone: false
})
export class LeaveImportComponent implements OnInit {
    parametersForm: UntypedFormGroup;
    tiposDeBajaFormArray: UntypedFormArray;
    templatesList: DropDownOption[] = [];
    selectedFileInBase64: string = null;

    lastLoadedTemplateID?: number = null;

    constructor(
        private dialog: MatDialog,
        private fb: UntypedFormBuilder,
        private leaveImportService: LeaveImportService,
        private sharedService: SharedService,
        private commonDialogService: CommonDialogService,
        private pageLoader: PageLoaderService,
        private fileService: FileExportService,
        private excelColumnsService: ExcelColumnsService
    ) {}

    ngOnInit(): void {
        this.initForm();
        this.listenTemplateListChanges();

        // create exmpty excel column list
        Promise.resolve(this.excelColumnsService.feedExcelData([]));
    }

    /**
     * Open Criterios dialog
     */
    openCriteriasDialog(): void {
        this.dialog.open(CriteriosComponent, {
            width: '100%',
            maxWidth: '90%',
        });
    }

    openImportarEmpresa() {
        const empresaId = parseInt(localStorage.getItem('Empresa'))
        let selectedEmpresaObj={
            empresaId:empresaId,
            empresa:localStorage.getItem('SelectedCompanyName')
        }
        this.dialog.open(ImportarEmpresaModalComponent, { data: selectedEmpresaObj });
    }

    //#region LEAVE IMPORT

    /**
     * Upload Excel file
     */
    public onUploadFile() {
        this.parametersForm.markAllAsTouched();
        if (!this.parametersForm.valid) return;
        if (this.selectedFileInBase64 === null) {
            this.sharedService.ShowSnackBar('Seleccione un archivo');
            return;
        }
    
        const tipoDeImportacion = this.parametersForm.controls.TipoDeImportacion.value;
        if (tipoDeImportacion === TipoDeImportacion.SoloTrabajadoresPlantilla) {
            this.commonDialogService
                .openConfirmationDialog('Se van a bloquear los trabajadores que no se encuentren en el Excel ¿Desea continuar?')
                .afterClosed()
                .subscribe((confirm) => {
                    if (confirm) {
                        this.proceedWithUpload(); 
                    }
                });
        } else {
            this.proceedWithUpload(); 
        }
    }

    private proceedWithUpload() {
        this.pageLoader.ShowPageLoader();
        const templateModel = this.buildTemplateModel();
        const empresaId = parseInt(localStorage.getItem('Empresa'));
        const importModel = new LeaveImportModel(
            this.parametersForm.controls.FechaAviso.value,
            empresaId,
            templateModel,
            this.selectedFileInBase64
        );
        this.importLeaves(importModel);
    }
    /**
     * Import leaves
     * @param importModel Leave import data model
     */
    private importLeaves(importModel: LeaveImportModel) {
        this.leaveImportService
            .importLeaves(importModel)
            .pipe(finalize(() => this.pageLoader.HidePageLoader()))
            .subscribe(
                (res) => {
                    if (res.code === 0) {
                        this.handleImportResults(res.result);
                    } else {
                        console.error('Server error: ' + res.message);
                        this.sharedService.ShowSnackBar(
                            'Lo siento, la importación de hojas ha fallado'
                        );
                    }
                },
                (err) => {
                    this.sharedService.ShowSnackBar(
                        'Lo siento, la importación de hojas ha fallado'
                    );
                    console.error(err);
                }
            );
    }

    /**
     * Handle results returned after a leave import
     * @param results Results after leave import
     */
    private handleImportResults(results: LeaveImportResult) {
        if (results.initialError === true) {
            this.sharedService.ShowSnackBar(results.message);
        } else {
            this.commonDialogService
                .openOkDialog(results.message)
                .afterClosed()
                .subscribe(() => {
                    if (results.errorFile) {
                        this.fileService.DownloadFile(
                            results.errorFile.file,
                            results.errorFile.fileName
                        );
                    }
                    if (results.activeLeavesFile) {
                        this.fileService.DownloadFile(
                            results.activeLeavesFile.file,
                            results.activeLeavesFile.fileName
                        );
                    }
                });
        }
    }

    //#endregion

    //#region INITIALIZATION

    /**
     * Initialize main form to its default values
     */
    private initForm() {
        this.parametersForm = this.fb.group({
            Formato: null,
            PrimeraFila: [2, [Validators.max(20000), Validators.min(1)]],
            ColumnaClave: [1, [Validators.max(200), Validators.min(1)]],
            Hojas: [1, [Validators.max(16), Validators.min(1)]],
            TipoDeImportacion: TipoDeImportacion.BajasAltas,
            AvisosDeBaja: AvisoBaja.NoneSelected,
            Tareas: [Tareas.SinTarea, Validators.required],
            Criterios: [null, this.criteriosValidator()],
            SinTelefono: SinTelefono.NoneSelected,
            ActualizarTrabajador: false,
            ActualizarReferencia: false,
            ActualizarIdentificadores: false,
            Dias: [1, [Validators.max(30), Validators.min(0)]],
            FechaAviso: null,
            ActualizarMobilidad: false,
            ActualizarEmpresa: false,
            ActualizarTipoBaja: false,
        });

        this.parametersForm.controls.Formato.valueChanges.subscribe((v) =>
            this.onTemplateSelect(v)
        );

        this.tiposDeBajaFormArray = this.fb.array([]);
    }

    /**
     * If Traeas is `Según criterios` then `Criterios de tareas` is required
     */
    private criteriosValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            return this.parametersForm?.controls.Tareas.value ===
                Tareas.SegunCriterios && control.value === null
                ? {
                      required:
                          'Los criterios de las tareas no están especificados',
                  }
                : null;
        };
    }

    /**
     * Start listening to changes in templates list
     */
    private listenTemplateListChanges() {
        this.leaveImportService.templatesList.subscribe(
            (res) => {
                // convert recieved template list into a drop down list
                const tempList: DropDownOption[] = [];
                for (const tId of Object.keys(res)) {
                    tempList.push({
                        label: res[tId],
                        value: parseInt(tId),
                    });
                }

                // sort by template name
                this.templatesList = _.orderBy(tempList, (t) => t.label);

                // remove whatever value was selected before
                this.parametersForm.controls.Formato.setValue(null);
            },
            (err) => {
                this.sharedService.ShowSnackBar(
                    'Error al obtener la lista de plantillas'
                );
                console.error('Error in getting templates list\n' + err);
            }
        );
    }

    //#endregion

    //#region TEMPLATE CREATION

    /**
     * Handle template save click
     */
    public onTemplateSaveClick() {
        this.parametersForm.markAllAsTouched();
        this.tiposDeBajaFormArray.markAllAsTouched();
        if (this.parametersForm.valid && this.tiposDeBajaFormArray.valid) {
            // create template
            this.createTemplate(this.buildTemplateModel());
        }
    }

    /**
     * Build a template model using current user input
     */
    private buildTemplateModel(): LeaveImportTemplate {
        const traesVal = this.parametersForm.controls.Tareas.value;
        const parameters = new Parameters(
            this.parametersForm.controls.PrimeraFila.value,
            this.parametersForm.controls.ColumnaClave.value,
            this.parametersForm.controls.Hojas.value,
            this.parametersForm.controls.TipoDeImportacion.value,
            this.parametersForm.controls.AvisosDeBaja.value,
            traesVal,
            traesVal === Tareas.SegunCriterios
                ? this.parametersForm.controls.Criterios.value
                : null,
            this.parametersForm.controls.SinTelefono.value,
            this.parametersForm.controls.ActualizarTrabajador.value ? 'S' : 'N',
            this.parametersForm.controls.ActualizarReferencia.value ? 'S' : 'N',
            this.parametersForm.controls.ActualizarIdentificadores.value ? 'S' : 'N',
            this.parametersForm.controls.ActualizarEmpresa.value ? 'S' : 'N',
            this.parametersForm.controls.ActualizarMobilidad.value ? 'S' : 'N',
            this.parametersForm.controls.ActualizarTipoBaja.value ? 'S' : 'N',
            this.parametersForm.controls.Dias.value,
        );

        return new LeaveImportTemplate(
            parameters,
            this.tiposDeBajaFormArray.value,
            this.excelColumnsService.getExcelData()
        );
    }

    /**
     * Create leave import template
     */
    private createTemplate(template: LeaveImportTemplate) {
        this.commonDialogService.openPromptDialog(
            'Nombre del formato',
            async (input) => {
                // if template name is already there, get user confirmation to replace it
                if (this.templatesList.find((t) => t.label === input)) {
                    const userResponse =
                        await this.getUserConfirmationToReplaceTemplate().toPromise();
                    if (!userResponse) return false;
                }

                return await this.onTemplateNameInput(input, template);
            }
        );
    }

    /**
     * Call this function when the user provides a name for the template. This function is made async for the custom usuability
     * @param templateName Template name provided by the user
     * @param templateData Template data model
     * @returns True if an error is not occured
     */
    private async onTemplateNameInput(
        templateName: string,
        templateData: LeaveImportTemplate
    ) {
        this.pageLoader.ShowPageLoader();

        // set templata name
        templateData.parameters.nombre = templateName;

        try {
            const res = await this.leaveImportService
                .createTemplate(templateData)
                .toPromise();

            if (res.code === 0) {
                this.pageLoader.HidePageLoader();

                // select new template
                this.parametersForm.controls.Formato.setValue(
                    res.result.parameters.importacionId
                );

                this.sharedService.ShowSnackBar('Se creó la plantilla');
                return true;
            } else {
                console.error('Server error: ' + res.message);
                this.sharedService.ShowSnackBar(
                    'Lo sentimos, no se pudo crear la plantilla'
                );
            }
        } catch (err) {
            this.sharedService.ShowSnackBar(
                'Lo sentimos, no se pudo crear la plantilla'
            );
            console.error(err);
        }

        this.pageLoader.HidePageLoader();
        return false;
    }

    /**
     * Get user confirmation regarding replacing the existing template
     */
    private getUserConfirmationToReplaceTemplate(): Observable<boolean> {
        return this.commonDialogService
            .openConfirmationDialog(
                'Este formato ya existe. ¿Deseas modificarlo?'
            )
            .afterClosed();
    }

    //#endregion

    //#region TEMPLATE DELETION

    /**
     * Handle template delete click
     */
    public onDeleteTemplateClick() {
        // ask confirmation to delete
        this.commonDialogService
            .openConfirmationDialog('¿Eliminar el formato?')
            .afterClosed()
            .subscribe((response) => {
                if (response) this.deleteSelectedTemplate();
            });
    }

    /**
     * Delete the currently selected template
     */
    private deleteSelectedTemplate() {
        this.pageLoader.ShowPageLoader();

        this.leaveImportService
            .deleteTemplate(this.parametersForm.controls.Formato.value)
            .subscribe(
                (res) => {
                    if (res.code === 0) {
                        // select first template
                        if (this.templatesList.length > 0)
                            this.parametersForm.controls.Formato.setValue(
                                this.templatesList[0].value
                            );
                        else this.pageLoader.HidePageLoader();

                        this.sharedService.ShowSnackBar(
                            'Se eliminó la plantilla'
                        );
                    } else {
                        this.pageLoader.HidePageLoader();
                        console.error('Server error: ' + res.message);
                        this.sharedService.ShowSnackBar(
                            'Lo sentimos, no se pudo borrar la plantilla'
                        );
                    }
                },
                (err) => {
                    this.pageLoader.HidePageLoader();
                    this.sharedService.ShowSnackBar(
                        'Lo sentimos, no se pudo borrar la plantilla'
                    );
                    console.error(err);
                }
            );
    }

    //#endregion

    //#region TEMPLATE SELECTION AND DATA LOADING

    /**
     * Listen for selected template ID changes
     * @param id Selected template ID
     */
    public onTemplateSelect(id: number) {
        if (id !== this.lastLoadedTemplateID) this.loadTemplateData(id);
    }

    /**
     * Load template data into fields
     * @param id Template
     */
    private loadTemplateData(id: number) {
        this.pageLoader.ShowPageLoader();

        this.leaveImportService.getTemplate(id).subscribe(
            (res) => {
                if (res.code === 0) {
                    this.lastLoadedTemplateID =
                        res.result.parameters.importacionId;
                    this.loadTemplateDataIntoFields(res.result);
                } else {
                    this.sharedService.ShowSnackBar(
                        'Error al cargar los datos de la plantilla'
                    );
                    console.error('Server error: ' + res.message);
                }

                this.pageLoader.HidePageLoader();
            },
            (err) => {
                this.pageLoader.HidePageLoader();
                this.sharedService.ShowSnackBar(
                    'Error al cargar los datos de la plantilla'
                );
                console.error(err);
            }
        );
    }

    /**
     * Load given template data into fields
     * @param templateData Template data
     */
    private loadTemplateDataIntoFields(templateData: LeaveImportTemplate) {
        // set parameter values
        this.parametersForm.patchValue({
            PrimeraFila: templateData.parameters.primeraFila,
            ColumnaClave: templateData.parameters.columnaClave,
            Hojas: templateData.parameters.hojas,
            TipoDeImportacion: templateData.parameters.tipoImportacion,
            AvisosDeBaja: templateData.parameters.avisosBaja,
            Tareas: templateData.parameters.tarea,
            SinTelefono: templateData.parameters.sinTelefono,
            ActualizarTrabajador:
                templateData.parameters.actualizarTrabajador === 'S',
            ActualizarReferencia:
                templateData.parameters.actualizarReferencia === 'S',
            ActualizarIdentificadores:
                templateData.parameters.actualizarIdentificadores === 'S',
            ActualizarMobilidad:
                templateData.parameters.actualizarMobilidad === 'S',
            ActualizarEmpresa:
                templateData.parameters.actualizarEmpresa === 'S',
            ActualizarTipoBaja:
                templateData.parameters.actualizarTipoBaja === 'S',
            Dias: templateData.parameters.diasPv,
            Formato:templateData.parameters.importacionId,
        });

        // set criterios value
        if (templateData.parameters.tarea === Tareas.SegunCriterios)
            this.parametersForm.controls.Criterios.setValue(
                templateData.parameters.criterioId
            );
        else this.parametersForm.controls.Criterios.setValue(null);

        // set leave types
        const newtiposDeBajaFormArray = templateData.leaveTypes.map((t) =>
            this.fb.group({
                tipoBaja: t.tipoBaja,
                excel: t.excel,
            })
        );
        this.tiposDeBajaFormArray = this.fb.array(newtiposDeBajaFormArray);

        // set excel column data
        this.excelColumnsService.feedExcelData(templateData.excelColumns);
    }

    //#endregion
}
